import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE, waLink } from "../lib/api";
import { Star, Quote, LayoutGrid, Rows3, Rows, ChevronLeft, ChevronRight, Sparkles, Share2, MessageCircle, BadgeCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Link } from "react-router-dom";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import Seo from "../lib/Seo";

const SERVICES = [
  "All",
  "Astrology Consultation",
  "Vedic Astrology Course",
  "Webinar",
  "Gemstone",
  "Rudraksha",
  "Other",
];

const fileUrl = (id) => `${API_BASE}/reviews/file/${id}`;

function StarsRow({ n = 5, size = "sm" }) {
  const px = size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="flex gap-1 text-[#D4AF37]">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`${px} ${i < n ? "fill-current" : "text-foreground/20"}`} />
      ))}
    </div>
  );
}

function ReviewCard({ r, size = "md" }) {
  return (
    <div
      data-testid={`review-${r.id}`}
      className={`relative p-6 md:p-7 rounded-3xl border ${r.pinned ? "border-[#D4AF37]" : "border-foreground/10"} bg-background overflow-hidden ${r.pinned ? "shadow-lg" : ""}`}
    >
      <Quote className="absolute -top-2 -right-2 w-20 h-20 text-[#D4AF37]/10 pointer-events-none" />
      <div className="flex items-center gap-3 mb-3 relative">
        {r.reviewer_avatar_url || r.photo_id ? (
          <img
            src={r.reviewer_avatar_url || fileUrl(r.photo_id)}
            alt={r.name}
            loading="lazy"
            className="w-11 h-11 rounded-full object-cover border border-[#D4AF37]/40"
          />
        ) : (
          <div className="w-11 h-11 rounded-full grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] font-semibold">
            {(r.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate flex items-center gap-2">
            {r.name}
            {r.verified_google && (
              <span title="Verified Google Review" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                <BadgeCheck className="w-3.5 h-3.5" />Google
              </span>
            )}
          </div>
          <div className="text-xs text-foreground/60 truncate">
            {r.city ? `${r.city} · ` : ""}{r.service}
          </div>
        </div>
        <StarsRow n={r.rating} />
      </div>

      <p className={`relative text-foreground/85 leading-relaxed ${size === "lg" ? "text-lg" : "text-[15px]"}`}>
        &ldquo;{r.review}&rdquo;
      </p>

      {r.video_id && (
        <video
          controls
          preload="metadata"
          className="mt-4 w-full rounded-2xl border border-foreground/10"
          src={fileUrl(r.video_id)}
        />
      )}

      <div className="mt-5 pt-4 border-t border-foreground/10 flex items-center justify-between text-[11px] uppercase tracking-widest text-foreground/50">
        <span>{(r.created_at || "").slice(0, 10)}</span>
        {r.verified_google && r.google_review_url ? (
          <a href={r.google_review_url} target="_blank" rel="noreferrer" className="text-emerald-600 dark:text-emerald-400 hover:underline">
            View on Google
          </a>
        ) : (
          <span className="text-[#D4AF37]">YourAstroHub</span>
        )}
      </div>
    </div>
  );
}

function Carousel({ items }) {
  const [i, setI] = useState(0);
  if (items.length === 0) return null;
  const prev = () => setI((v) => (v - 1 + items.length) % items.length);
  const next = () => setI((v) => (v + 1) % items.length);
  return (
    <div>
      <div className="mx-auto max-w-3xl"><ReviewCard r={items[i]} size="lg" /></div>
      <div className="mt-6 flex items-center justify-between max-w-3xl mx-auto">
        <div className="flex gap-1.5">
          {items.slice(0, 12).map((_, k) => (
            <button key={k} onClick={() => setI(k)} data-testid={`carousel-dot-${k}`}
              className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-[#D4AF37]" : "w-3 bg-foreground/20 hover:bg-foreground/40"}`} />
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={prev} data-testid="carousel-prev" className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={next} data-testid="carousel-next" className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

const LAYOUTS = [
  { key: "grid", label: "Grid", icon: LayoutGrid },
  { key: "masonry", label: "Masonry", icon: Rows3 },
  { key: "carousel", label: "Carousel", icon: Rows },
];

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, average_rating: 0, distribution: {} });
  const [service, setService] = useState("All");
  const [rating, setRating] = useState("all");
  const [q, setQ] = useState("");
  const [layout, setLayout] = useState("grid");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (service && service !== "All") params.set("service", service);
    if (rating && rating !== "all") params.set("rating", String(rating));
    if (q) params.set("q", q);
    setLoading(true);
    api.get(`/reviews?${params}`).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, [service, rating, q]);

  useEffect(() => {
    api.get("/reviews/stats").then((r) => setStats(r.data));
  }, []);

  const aggregate = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "YourAstroHub",
    aggregateRating: stats.total > 0 ? {
      "@type": "AggregateRating",
      ratingValue: stats.average_rating,
      reviewCount: stats.total,
      bestRating: 5,
      worstRating: 1,
    } : undefined,
    review: (items || []).slice(0, 20).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      reviewBody: r.review,
      datePublished: (r.created_at || "").slice(0, 10),
    })),
  }), [items, stats]);

  return (
    <div data-testid="testimonials-page" className="min-h-screen">
      <Seo
        title="Client & Student Testimonials — YourAstroHub Reviews"
        description="Real Google-verified and website reviews from Vedic astrology consultation clients and Jyotish course students of Bhupaal Bisht at YourAstroHub."
        keywords="YourAstroHub Reviews, Bhupaal Bisht Reviews, Vedic Astrology Testimonials, Jyotish Course Reviews"
        path="/testimonials"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Testimonials", path: "/testimonials" },
        ]}
        schemas={[aggregate]}
      />

      {/* HERO */}
      <section className="pt-10 pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow mb-4">Words from Our Seekers</div>
            <h1 className="font-serif text-4xl md:text-6xl font-light leading-[1.05]">
              Real voices. <span className="gold-text italic">Real change.</span>
            </h1>
            <p className="text-foreground/70 mt-5 text-base md:text-lg max-w-xl">
              Feedback from Vedic astrology consultation clients, course students, webinar attendees and Rudraksha seekers.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/feedback" data-testid="cta-share-experience">
                <Button className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-7 py-5">
                  <Sparkles className="w-4 h-4 mr-2" />Share your experience
                </Button>
              </Link>
              <a href={waLink("Hello, I would like to share my feedback with YourAstroHub.")} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 px-7 py-5">
                  <MessageCircle className="w-4 h-4 mr-2" />Share on WhatsApp
                </Button>
              </a>
            </div>
          </div>

          {/* Stats block */}
          <div className="md:col-span-5">
            <div className="rounded-3xl border border-[#D4AF37]/40 p-6 md:p-8 bg-[#0B1F3A] text-[#F5E8D8] relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#D4AF37]/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-baseline gap-3">
                  <span className="font-serif text-6xl gold-text">{stats.average_rating || "—"}</span>
                  <StarsRow n={Math.round(stats.average_rating || 0)} size="lg" />
                </div>
                <div className="text-[#F5E8D8]/70 text-sm mt-1" data-testid="stats-total">
                  {stats.total || 0} verified reviews · Updated live
                </div>
                <div className="mt-5 space-y-2">
                  {[5, 4, 3, 2, 1].map((n) => {
                    const c = Number(stats.distribution?.[String(n)] || 0);
                    const pct = stats.total ? Math.round((c / stats.total) * 100) : 0;
                    return (
                      <div key={n} className="flex items-center gap-3">
                        <span className="text-xs w-4">{n}</span>
                        <Star className="w-3.5 h-3.5 text-[#D4AF37]" />
                        <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-[#F5E8D8]/60 w-9 text-right">{c}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS + LAYOUT */}
      <section className="py-6 border-y border-foreground/10 bg-[#F4EFE6]/50 dark:bg-[#0F284B]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-wrap items-center gap-3">
          <Select value={service} onValueChange={setService}>
            <SelectTrigger data-testid="filter-service" className="rounded-full w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All services" : s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={rating} onValueChange={setRating}>
            <SelectTrigger data-testid="filter-rating" className="rounded-full w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? "s" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Search reviews…" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="filter-search" className="rounded-full max-w-xs" />
          <div className="ml-auto flex items-center gap-1 rounded-full border border-foreground/10 p-1">
            {LAYOUTS.map((l) => (
              <button key={l.key} onClick={() => setLayout(l.key)} data-testid={`layout-${l.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${
                  layout === l.key ? "bg-[#D4AF37] text-[#0B1F3A]" : "text-foreground/60 hover:text-foreground"
                }`}>
                <l.icon className="w-3.5 h-3.5" />{l.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS DISPLAY */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          {loading ? (
            <div className="text-center text-foreground/50 py-24">Loading reviews…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-24">
              <div className="font-serif text-3xl mb-2">Your review could be here.</div>
              <p className="text-foreground/60 max-w-md mx-auto">Be one of the first to share your experience — every review helps another seeker find the right path.</p>
              <Link to="/feedback" className="inline-block mt-6">
                <Button className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A]">Share yours</Button>
              </Link>
            </div>
          ) : layout === "carousel" ? (
            <Carousel items={items} />
          ) : layout === "masonry" ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]" data-testid="layout-masonry-wrap">
              {items.map((r) => (
                <div key={r.id} className="break-inside-avoid mb-6">
                  <ReviewCard r={r} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="layout-grid-wrap">
              {items.map((r) => <ReviewCard key={r.id} r={r} />)}
            </div>
          )}
        </div>
      </section>

      {/* SHARE BAND */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="rounded-3xl p-8 md:p-12 border border-[#D4AF37]/40 bg-background text-center">
            <Share2 className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <h3 className="font-serif text-2xl md:text-3xl">Loved your session? <span className="gold-text italic">Tell the world.</span></h3>
            <p className="mt-2 text-foreground/70 max-w-xl mx-auto">Your words help another seeker take the first step.</p>
            <Link to="/feedback" className="inline-block mt-5">
              <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                Share your experience
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
